import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { ViewList as ViewListIcon, ViewModule as ViewModuleIcon } from '@mui/icons-material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageFiltersSection,
} from '../../components/common/list-page';
import InventoryStats from '../../components/inventory/InventoryStats/index';
import InventoryQueryFilters from '../../components/inventory/InventoryQueryFilters/index';
import { Category } from '../../api/models/Category';
import { Tag } from '../../api/models/Tag';
import { CategoriesService } from '../../api/services/CategoriesService';
import { TagsService } from '../../api/services/TagsService';
import { MaterialsService } from '../../api/services/MaterialsService';
import { ColorsService } from '../../api/services/ColorsService';
import {
  InventoryItem,
  PaginationInfo,
  InventoryFilterState,
  DEFAULT_WAREHOUSE_INVENTORY_SORT,
  WarehouseInventorySortField,
} from '../../types/inventory';
import { WarehouseInventoryProductsService } from '../../api/services/WarehouseInventoryProductsService';
import { UpdateAvailabilityStatusDto } from '../../api/models/UpdateAvailabilityStatusDto';
import { LocationsService } from '../../api/services/LocationsService';
import { Location } from '../../api/models/Location';
import { WarehouseInventorySummaryDto } from '../../api/models/WarehouseInventorySummaryDto';
import InventoryDetailModal from '../../components/inventory/InventoryDetailModal/index';
import InventoryDrawer from '../../components/inventory/InventoryDrawer';
import { WarehouseTable, InventoryGrid } from '../../components/inventory/InventoryQueryTable';
import InventoryTransferModal from '../../components/inventory/InventoryTransferModal/index';
import {
  ReserveConfirmationModal,
  UnreserveConfirmationModal,
} from '../../components/modals/confirmation';
import CommonPagination from '../../components/common/Pagination';
import { INVENTORY_MESSAGES } from '../../utils/constants';
import EmptyStateMessage from '../../components/common/EmptyStateMessage';
import { printInventoryUnitLabel } from '../../utils/printInventoryLabels';
import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
  isAvailabilityWrittenOff,
} from '../../constants/inventory-status-labels';
import {
  buildWarehouseInventoryFindAllArgs,
  warehouseQueryPlacementStatus,
} from '../../utils/inventory/warehouseInventoryListQuery';
import { mapWarehouseInventoryProductsToInventoryItems } from '../../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import { useInventoryWarehousePermissions } from '../../hooks/useInventoryWarehousePermissions';
import { useInventoryTransferScan } from '../../hooks/useInventoryTransferScan';

const InventoryQueryPage: React.FC = () => {
  const warehousePerms = useInventoryWarehousePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [inventoryStats, setInventoryStats] = useState<WarehouseInventorySummaryDto>({
    totalProducts: 0,
    totalValue: 0,
    totalCostValue: 0,
    writeOffCount: 0,
    statusSummary: [],
    availabilityStatusSummary: [],
    mostCommonCondition: '',
    lastActivityDate: '',
  });
  const [isStatsLoading, setIsStatsLoading] = useState<boolean>(true);

  // Dynamic data for filters
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagsList, setTagsList] = useState<Tag[]>([]);
  const [materials, setMaterials] = useState<{ id: number; name: string }[]>([]);
  const [colorsCatalog, setColorsCatalog] = useState<{ id: number; name: string }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMetadataLoading, setIsMetadataLoading] = useState<boolean>(true);

  // Using the new filter state format
  const [filters, setFilters] = useState<InventoryFilterState>({
    searchTerm: '',
    createdAtStart: '',
    createdAtEnd: '',
    status: '',
    locationId: '',
    categoryName: '',
    recordCode: '',
    searchTag: '',
    materialId: '',
    diameterSize: '',
    color: '',
    workshopCode: '',
    weightGramsMin: undefined,
    weightGramsMax: undefined,
    salePriceMin: undefined,
    salePriceMax: undefined,
    salePricePerGramMin: undefined,
    salePricePerGramMax: undefined,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [inventorySort, setInventorySort] = useState<{
    sortBy: WarehouseInventorySortField;
    sortOrder: 'asc' | 'desc';
  }>(DEFAULT_WAREHOUSE_INVENTORY_SORT);

  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  // Estado para elementos seleccionados (visual UI)
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // Estado para realizar un seguimiento de los IDs numéricos correspondientes
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedOriginalIds, setSelectedOriginalIds] = useState<{ [key: string]: number }>({});

  // Nuevo estado para almacenar los detalles completos de los productos seleccionados
  const [selectedItemsDetails, setSelectedItemsDetails] = useState<InventoryItem[]>([]);

  // Estado para el modal de transferencia
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);
  const [isUnreserveModalOpen, setIsUnreserveModalOpen] = useState(false);
  const [itemToReserve, setItemToReserve] = useState<InventoryItem | null>(null);
  const [isReserving, setIsReserving] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Add a search trigger state to control when searches are executed
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Add state for statuses with the correct backend values
  const [statuses] = useState([
    { id: 'UNASSIGNED', name: INV_STATUS_WAREHOUSE_LABEL },
    { id: 'IN_TRANSIT', name: 'En Tránsito' },
  ]);

  const [locations, setLocations] = useState<Location[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLocationsLoading, setIsLocationsLoading] = useState<boolean>(true);

  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  // Fetch inventory stats from API
  const fetchInventoryStats = useCallback(async (currentFilters: InventoryFilterState) => {
    setIsStatsLoading(true);
    try {
      const response =
        await WarehouseInventoryProductsService.warehouseInventoryProductsControllerGetSummary(
          currentFilters.locationId ? Number(currentFilters.locationId) : undefined, // locationId (number)
          undefined, // productCode
          warehouseQueryPlacementStatus(currentFilters.status),
          undefined, // transferredToBranch
          undefined, // productCodeUnique
          undefined, // productName
          currentFilters.searchTerm || undefined, // searchTerm
          currentFilters.categoryName || undefined, // categoryName (Tipo de producto)
          currentFilters.createdAtStart || undefined,
          currentFilters.createdAtEnd || undefined,
          currentFilters.recordCode || undefined, // recordCode (Número de registro)
          undefined, // availabilityStatus (removed)
          currentFilters.searchTag || undefined, // searchTag (Etiqueta)
          currentFilters.materialId ? Number(currentFilters.materialId) : undefined, // materialId
          currentFilters.diameterSize || undefined, // diameterSize
          currentFilters.color || undefined, // color
          currentFilters.workshopCode || undefined, // workshopCode
          currentFilters.weightGramsMin,
          currentFilters.weightGramsMax,
          currentFilters.salePriceMin,
          currentFilters.salePriceMax,
          currentFilters.salePricePerGramMin,
          currentFilters.salePricePerGramMax,
          true, // excludeCancelledAvailability — bajas solo en sucursal
          true, // warehousePlacementOnly — solo almacén y tránsito
          undefined, // assignedPlacementOnly — solo gestión en sucursal
        );
      setInventoryStats(response);
    } catch (error) {
      console.error('Error fetching inventory stats:', error);
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  // Fetch data from API using the new filter format - now takes filters as parameter to avoid stale closure
  const fetchInventoryData = useCallback(
    async (currentFilters: InventoryFilterState) => {
      setIsLoading(true);

      try {
        const response =
          await WarehouseInventoryProductsService.warehouseInventoryProductsControllerFindAll(
            ...buildWarehouseInventoryFindAllArgs({
              page: currentPage,
              limit: itemsPerPage,
              filters: currentFilters,
              placementStatus: warehouseQueryPlacementStatus(currentFilters.status),
              sortBy: inventorySort.sortBy as string,
              sortOrder: inventorySort.sortOrder,
              scope: {
                excludeCancelledAvailability: true,
                warehousePlacementOnly: true,
              },
              includeNumericFilters: true,
            }),
          );

        const mappedItems = mapWarehouseInventoryProductsToInventoryItems(response.data, {
          mapTransferFields: false,
        });
        setInventory(mappedItems);

        // Match the actual API response structure (meta.total instead of meta.totalItems)
        const totalItems = response.meta?.total || 0;
        setTotalItems(totalItems);

        // If we have no data and we're not on page 1, go back to the last valid page
        if (mappedItems.length === 0 && currentPage > 1 && totalItems > 0) {
          const totalPages = Math.ceil(totalItems / itemsPerPage);
          // If current page is beyond total pages, go to the last page
          if (currentPage > totalPages) {
            setCurrentPage(totalPages);
          }
        }
      } catch (error) {
        console.error('Error fetching inventory data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, itemsPerPage, inventorySort.sortBy, inventorySort.sortOrder],
  );

  const handleInventorySortChange = useCallback((field: WarehouseInventorySortField) => {
    setInventorySort((prev) => {
      if (prev.sortBy === field) {
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      }
      return { sortBy: field, sortOrder: 'asc' };
    });
    setCurrentPage(1);
  }, []);

  // Store current filters in a ref to access in useEffect
  const currentFiltersRef = useRef(filters);
  useEffect(() => {
    currentFiltersRef.current = filters;
  }, [filters]);

  // Load initial data - triggered by searchTrigger instead of automatic filter dependency
  useEffect(() => {
    fetchInventoryData(currentFiltersRef.current);
    fetchInventoryStats(currentFiltersRef.current);
  }, [fetchInventoryData, fetchInventoryStats, searchTrigger]);

  // Initial search trigger
  useEffect(() => {
    setSearchTrigger(1);
  }, []);

  // Refresh stats when filters change (only when necessary for stats)
  useEffect(() => {
    // Only refresh stats when relevant filters change
    if (filters.status) {
      fetchInventoryStats(currentFiltersRef.current);
    }
  }, [filters.status, fetchInventoryStats]);

  // Paginación
  const paginationInfo: PaginationInfo = useMemo(() => {
    return {
      currentPage,
      totalPages: Math.ceil(totalItems / itemsPerPage),
      itemsPerPage,
      totalItems,
    };
  }, [currentPage, itemsPerPage, totalItems]);

  // Update to handle the new filter format - no longer auto-triggers search
  const handleFilterChange = useCallback((newFilters: InventoryFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Resetear a la primera página cuando se aplican filtros
    // Trigger search by incrementing searchTrigger
    setSearchTrigger((prev) => prev + 1);
  }, []);

  // Manejar cambio de página
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  // Manejar cambio en items por página
  const handleItemsPerPageChange = useCallback((newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Resetear a la primera página
  }, []);

  // Manejar click en ver detalles
  const handleViewDetails = (id: string) => {
    // Find the item with the given id
    const item = inventory.find((item) => item.id === id);
    if (item) {
      setSelectedItem(item);
      setIsDetailModalOpen(true);
    }
  };

  // Manejar click en editar: abre el modal de detalle (igual que gestión en sucursal)
  const handleEditItem = (id: string) => {
    const item = inventory.find((item) => item.id === id);
    if (item) {
      setSelectedItem(item);
      setIsDetailModalOpen(true);
    }
  };

  // Manejar guardar cambios
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveItem = async (updatedItem: any) => {
    // API call to save the updated item
    // After successful save, update your state/refresh data
  };

  // Convert string arrays to object arrays for the filter component
  const formattedCategories = useMemo(() => {
    return productTypes
      .filter((tipo) => tipo !== 'Todos')
      .map((tipo, index) => ({
        id: index + 1,
        name: tipo,
      }));
  }, [productTypes]);

  const formattedLocations = useMemo(() => {
    return locations.map((location) => ({
      id: location.id,
      name: location.name,
      type: location.type,
    }));
  }, [locations]);

  const formattedTags = useMemo(() => {
    return tagsList.map((tag) => ({
      id: tag.id,
      name: tag.name,
    }));
  }, [tagsList]);

  const formattedItemTypes = useMemo(() => {
    return productTypes.map((tipo, index) => ({
      id: index + 1,
      name: tipo,
    }));
  }, [productTypes]);

  const formattedItemMaterials = useMemo(() => {
    return tags.map((etiqueta, index) => ({
      id: index + 1,
      name: etiqueta,
    }));
  }, [tags]);

  const formattedBranches = useMemo(() => {
    // Example branches - replace with actual data if available
    return [
      { id: 1, name: 'Sucursal Principal' },
      { id: 2, name: 'Sucursal Norte' },
      { id: 3, name: 'Sucursal Sur' },
    ];
  }, []);

  const formattedItemStatuses = useMemo(() => {
    return [
      { id: 'assigned', name: INV_STATUS_BRANCH_LABEL },
      { id: 'unassigned', name: INV_STATUS_WAREHOUSE_LABEL },
    ];
  }, []);

  // Actualizar selectedOriginalIds cuando cambia el inventario
  useEffect(() => {
    // Construir un mapa de ID visual -> ID numérico
    const idMap: { [key: string]: number } = {};
    inventory.forEach((item) => {
      if (item.id && item.originalId) {
        idMap[item.id] = item.originalId;
      }
    });

    // Actualizar el estado con los nuevos IDs
    setSelectedOriginalIds((prevIds) => ({
      ...prevIds,
      ...idMap,
    }));
  }, [inventory]);

  // Actualizar los detalles del producto cuando cambia el inventario o las selecciones
  useEffect(() => {
    // Cuando cambia el inventario, actualizamos los detalles de los productos seleccionados
    const newDetails = [...selectedItemsDetails];
    let changed = false;

    // Agregar nuevos elementos seleccionados que no estén en los detalles
    inventory.forEach((item) => {
      if (selectedItems.includes(item.id)) {
        // Verificar si este item ya está en los detalles
        const existingIndex = newDetails.findIndex((detail) => detail.id === item.id);
        if (existingIndex === -1) {
          // Si no existe, lo agregamos
          newDetails.push(item);
          changed = true;
        }
      }
    });

    // Solo actualizar el estado si hubo cambios
    if (changed) {
      setSelectedItemsDetails(newDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, selectedItems]);

  // Manejar selección de elementos para transferencia
  const handleSelectItems = (selectedIds: string[]) => {
    setSelectedItems(selectedIds);

    // Si hay elementos deseleccionados, los eliminamos de los detalles
    setSelectedItemsDetails((prev) => prev.filter((item) => selectedIds.includes(item.id)));
  };

  const handleSelectScannedItem = useCallback((item: InventoryItem) => {
    setSelectedItems((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
    setSelectedItemsDetails((prev) =>
      prev.some((detail) => detail.id === item.id) ? prev : [...prev, item],
    );
  }, []);

  const handleScanNotify = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
      setNotification({ open: true, message, severity });
    },
    [],
  );

  const { handleScanSubmit } = useInventoryTransferScan({
    context: 'warehouse',
    getFilters: () => currentFiltersRef.current,
    selectedItems,
    onSelectItem: handleSelectScannedItem,
    onNotify: handleScanNotify,
    enabled: warehousePerms.canTransfer,
  });

  // Obtener todos los IDs numéricos para los elementos seleccionados
  const getNumericIdsForSelected = (): string[] => {
    // La función necesita pasar los IDs originales tal como fueron seleccionados
    // porque el modal se encargará de mapearlos correctamente
    return selectedItems;
  };

  // Manejar la apertura del modal de transferencia
  const handleOpenTransferModal = () => {
    if (selectedItems.length > 0) {
      const numericIds = getNumericIdsForSelected();

      if (numericIds.length === 0) {
        console.error(
          'No se pudo determinar ningún ID numérico válido. No se pueden transferir las piezas.',
        );
        return;
      }

      setIsTransferModalOpen(true);
    }
  };

  // Manejar el cierre del modal de transferencia
  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
  };

  // Manejar el éxito de la transferencia
  const handleTransferSuccess = () => {
    setSelectedItems([]);
    setIsTransferModalOpen(false);
    fetchInventoryData(currentFiltersRef.current);
    fetchInventoryStats(currentFiltersRef.current);
  };

  const handleReserveItem = (id: string) => {
    const item = inventory.find((i) => i.id === id);
    if (!item) return;
    setItemToReserve(item);
    if (item.availabilityStatus === 'Disponible') {
      setIsReserveModalOpen(true);
    } else if (item.availabilityStatus === INV_AVAILABILITY_BLOCKED_LABEL) {
      setIsUnreserveModalOpen(true);
    }
  };

  const handleConfirmReserve = async (comment?: string) => {
    if (!itemToReserve?.originalId) return;
    setIsReserving(true);
    try {
      await WarehouseInventoryProductsService.warehouseInventoryProductsControllerUpdateAvailabilityStatus(
        itemToReserve.originalId,
        {
          status: UpdateAvailabilityStatusDto.status.RESERVED,
          ...(comment ? { comment } : {}),
        },
      );
      setIsReserveModalOpen(false);
      setItemToReserve(null);
      setSearchTrigger((prev) => prev + 1);
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'body' in error
          ? (error as { body?: { message?: string } }).body?.message
          : undefined;
      setNotification({
        open: true,
        ...INVENTORY_MESSAGES.RESERVE_ERROR(message),
      });
    } finally {
      setIsReserving(false);
    }
  };

  const handleConfirmUnreserve = async (comment?: string) => {
    if (!itemToReserve?.originalId) return;
    setIsReserving(true);
    try {
      await WarehouseInventoryProductsService.warehouseInventoryProductsControllerUpdateAvailabilityStatus(
        itemToReserve.originalId,
        {
          status: UpdateAvailabilityStatusDto.status.AVAILABLE,
          ...(comment ? { comment } : {}),
        },
      );
      setIsUnreserveModalOpen(false);
      setItemToReserve(null);
      setSearchTrigger((prev) => prev + 1);
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'body' in error
          ? (error as { body?: { message?: string } }).body?.message
          : undefined;
      setNotification({
        open: true,
        ...INVENTORY_MESSAGES.RELEASE_RESERVE_ERROR(message),
      });
    } finally {
      setIsReserving(false);
    }
  };

  // Manejar la eliminación de un producto en el modal
  const handleRemoveProductFromModal = (productId: string) => {
    // Encontrar el código del producto (ID visual) a partir del ID numérico
    const visualId = selectedItemsDetails.find((item) => item.id === productId)?.id;

    if (visualId && selectedItems.includes(visualId)) {
      setSelectedItems((prevSelected) => prevSelected.filter((id) => id !== visualId));

      // También eliminarlo de los detalles
      setSelectedItemsDetails((prev) => prev.filter((item) => item.id !== visualId));
    }
  };

  // Add function to fetch locations
  const fetchLocations = useCallback(async () => {
    setIsLocationsLoading(true);
    try {
      const response = await LocationsService.locationsControllerFindAll();
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setIsLocationsLoading(false);
    }
  }, []);

  // Fetch metadata for filters
  const fetchMetadata = useCallback(async () => {
    setIsMetadataLoading(true);
    try {
      const [categoriesResponse, tagsResponse, materialsResponse, colorsResponse] =
        await Promise.all([
          CategoriesService.categoriesControllerFindAll(1, 1000),
          TagsService.tagsControllerFindAll(1, 1000),
          MaterialsService.materialsControllerFindAll(1, 1000),
          ColorsService.colorsControllerFindAll(1, 1000),
        ]);

      const categoriesList: Category[] = Array.isArray(categoriesResponse?.data)
        ? categoriesResponse.data
        : Array.isArray(categoriesResponse)
          ? categoriesResponse
          : [];
      setProductTypes([...categoriesList.map((cat) => cat.name)]);

      const tagsList: Tag[] = Array.isArray(tagsResponse?.data)
        ? tagsResponse.data
        : Array.isArray(tagsResponse)
          ? tagsResponse
          : [];
      setTags(['Todas', ...tagsList.map((tag) => tag.name)]);
      setTagsList(tagsList);

      const materialsList = Array.isArray(materialsResponse?.data)
        ? materialsResponse.data
        : Array.isArray(materialsResponse)
          ? materialsResponse
          : [];
      setMaterials(
        materialsList.map((material: any) => ({
          id: material.id,
          name: material.name,
        })),
      );

      const colorsList = Array.isArray(colorsResponse?.data)
        ? colorsResponse.data
        : Array.isArray(colorsResponse)
          ? colorsResponse
          : [];
      setColorsCatalog(
        colorsList.map((c: any) => ({
          id: c.id,
          name: c.name,
        })),
      );
    } catch (error) {
      console.error('Error fetching metadata:', error);
      setProductTypes([]);
      setTags(['Todas']);
      setMaterials([]);
      setColorsCatalog([]);
    } finally {
      setIsMetadataLoading(false);
    }
  }, []);

  // Fetch locations and metadata when component mounts
  useEffect(() => {
    fetchLocations();
    fetchMetadata();
  }, [fetchLocations, fetchMetadata]);

  const pageContent = (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        {/* Dashboard Informativo */}
        {isStatsLoading ? (
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '120px',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <InventoryStats
              totalProducts={inventoryStats.totalProducts}
              totalValue={inventoryStats.totalValue}
              totalCostValue={inventoryStats.totalCostValue}
              writeOffCount={inventoryStats.writeOffCount ?? 0}
              statusSummary={inventoryStats.statusSummary}
              availabilityStatusSummary={inventoryStats.availabilityStatusSummary}
              lastActivityDate={inventoryStats.lastActivityDate}
              variant="warehouse"
              compactSpacing
            />
          </Box>
        )}

        <ListPageFiltersSection>
          <InventoryQueryFilters
            categories={formattedCategories}
            locations={formattedLocations}
            statuses={statuses}
            tags={formattedTags}
            colors={colorsCatalog}
            materials={materials}
            onFilterChange={handleFilterChange}
            initialFilters={filters}
            selectedItems={selectedItems}
            onTransferClick={handleOpenTransferModal}
            onScanSubmit={warehousePerms.canTransfer ? handleScanSubmit : undefined}
            isInventoryWarehouseQuery
            showPieceNumericFilters
            hideSecondaryFilterPlaceholders
            rightControls={
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode: 'table' | 'grid' | null) =>
                  newMode != null && setViewMode(newMode)
                }
                aria-label="vista de inventario"
                size="small"
                sx={(theme) => ({
                  '& .MuiToggleButton-root': {
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.secondary,
                    '&.Mui-selected': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(6, 182, 212, 0.2)'
                          : 'rgba(6, 182, 212, 0.12)',
                      color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#0891b2',
                      borderColor: theme.palette.info.main,
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(6, 182, 212, 0.28)'
                            : 'rgba(6, 182, 212, 0.18)',
                      },
                    },
                  },
                })}
              >
                <ToggleButton value="table" aria-label="vista tabla">
                  <Tooltip title="Vista tabla" arrow>
                    <ViewListIcon />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="grid" aria-label="vista cuadrícula">
                  <Tooltip title="Vista cuadrícula" arrow>
                    <ViewModuleIcon />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            }
          />
        </ListPageFiltersSection>

        {/* Loading indicator */}
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tabla de Inventario - Opción 4: Sin Paper, borde y fondo sutil */}
            {inventory.length > 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  overflow: 'hidden',
                  minHeight: 0,
                  px: 0,
                }}
              >
                {viewMode === 'table' ? (
                  <WarehouseTable
                    items={inventory}
                    onViewDetails={handleViewDetails}
                    onEditItem={handleEditItem}
                    pagination={paginationInfo}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    selectedItems={selectedItems}
                    onSelectItems={handleSelectItems}
                    onTransferSelected={handleOpenTransferModal}
                    hidePagination={true}
                    hideTransferColumns
                    hideLocationColumn
                    onPrintLabel={printInventoryUnitLabel}
                    sortBy={inventorySort.sortBy}
                    sortOrder={inventorySort.sortOrder}
                    onSortChange={handleInventorySortChange}
                    onReserveItem={handleReserveItem}
                  />
                ) : (
                  <InventoryGrid
                    variant="warehouse"
                    items={inventory}
                    onViewDetails={handleViewDetails}
                    onEditItem={handleEditItem}
                    selectedItems={selectedItems}
                    onSelectItems={handleSelectItems}
                    onTransferSelected={handleOpenTransferModal}
                    hideLocationColumn
                    onPrintLabel={printInventoryUnitLabel}
                    onReserveItem={handleReserveItem}
                  />
                )}
              </Box>
            )}

            {/* Empty state */}
            {inventory.length === 0 && !isLoading && (
              <EmptyStateMessage message="No se encontraron piezas en inventario" minHeight={200} />
            )}

            {/* Paginación uniforme - Opción 4: Sin Paper, sin borde superior */}
            {inventory.length > 0 && (
              <Box>
                <CommonPagination
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={(event) => {
                    const newItemsPerPage = parseInt(event.target.value as string);
                    handleItemsPerPageChange(newItemsPerPage);
                  }}
                />
              </Box>
            )}
          </>
        )}
      </ListPageMain>

      {/* Modal de detalle */}
      <InventoryDetailModal
        open={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        item={selectedItem}
        onEditItem={handleEditItem}
        allowEditInWarehouse={
          warehousePerms.canEdit && !isAvailabilityWrittenOff(selectedItem?.availabilityStatus)
        }
        onSave={() => {
          fetchInventoryData(currentFiltersRef.current);
          fetchInventoryStats(currentFiltersRef.current);
        }}
        onItemUpdate={(updatedItem) => {
          setSelectedItem(updatedItem);
          setInventory((prevInventory) =>
            prevInventory.map((item) => (item.id === updatedItem.id ? updatedItem : item)),
          );
        }}
        blockHistoryRefreshKey={searchTrigger}
      />

      {/* Modal de transferencia */}
      <InventoryTransferModal
        open={isTransferModalOpen}
        onClose={handleCloseTransferModal}
        selectedProductIds={selectedItems}
        inventoryItems={selectedItemsDetails}
        onSuccess={handleTransferSuccess}
        locations={formattedLocations.map((loc) => ({
          id: loc.id,
          name: loc.name,
          type: loc.type,
        }))}
        onRemoveProduct={handleRemoveProductFromModal}
      />

      {/* Drawer de edición */}
      <InventoryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleSaveItem}
        itemTypes={formattedItemTypes}
        itemMaterials={formattedItemMaterials}
        branches={formattedBranches}
        itemStatuses={formattedItemStatuses}
        editItem={selectedItem}
      />

      <ReserveConfirmationModal
        open={isReserveModalOpen}
        onClose={() => setIsReserveModalOpen(false)}
        onConfirm={handleConfirmReserve}
        itemName={itemToReserve?.name}
        itemCode={itemToReserve?.id}
        isLoading={isReserving}
        message="¿Bloquear esta pieza? No podrá transferirse hasta que libere el bloqueo."
      />

      <UnreserveConfirmationModal
        open={isUnreserveModalOpen}
        onClose={() => setIsUnreserveModalOpen(false)}
        onConfirm={handleConfirmUnreserve}
        itemName={itemToReserve?.name}
        itemCode={itemToReserve?.id}
        isLoading={isReserving}
        message="¿Liberar el bloqueo? La pieza volverá a estar disponible para transferencia."
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification((n) => ({ ...n, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ListPageScaffold>
  );

  return pageContent;
};

export default InventoryQueryPage;
